import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { inAppBrowser, iosNotSafari, isIOS, registerPush, testTapped, thisEndpoint, useDevice } from '@/lib/device';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { CheckIcon } from '@/components/Icons';
import { AlertCheck } from '@/components/AlertCheck';
import { HomeScreenSteps, OpenInBrowser } from '@/components/HomeScreenSteps';

// First run on a phone (Gary, 19 Oct): iPhone, home screen first; then allow alerts; then tap Sentinel's test alert,
// which is what makes Sentinel count the phone for new leads. Android needs no home screen. Never a trap: "Not now"
// is always there. Phone settings show only after a test that did not arrive (AlertCheck's No).

/** One numbered step. Shared with the step-by-step guide (GuideScreen). */
export function Step({ n, done, title, id, children }: { n: number; done: boolean; title: string; id?: string; children?: ReactNode }) {
  return (
    <div id={id} className={`chats-step${done ? ' chats-step--done' : ''}`}>
      <span className="chats-step-n" aria-hidden="true">{done ? <CheckIcon /> : n}</span>
      <div className="chats-step-body">
        <b>{title}</b>
        {children}
      </div>
    </div>
  );
}

export function SetupScreen({ onDone }: { onDone: () => void }) {
  const { standalone, notify, canInstall, install, askNotifications } = useDevice();
  const { session } = useAuth();
  const [sent, setSent] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [round, setRound] = useState(0);
  // Set-up finishes on the tap of Sentinel's test alert (Gary, 19 Oct): only then does Sentinel count this phone for
  // new leads. The tap opens the app with ?from=push&test=1 (lib/device.ts), heard here at once.
  const [tapped, setTapped] = useState(() => testTapped(session?.user.id));
  useEffect(() => {
    const on = () => setTapped(true);
    window.addEventListener('chats-test-tapped', on);
    return () => window.removeEventListener('chats-test-tapped', on);
  }, []);
  const notifyOn = notify === 'granted';
  // iPhone alerts work only from the home screen; Android's work in Chrome, so its home screen step is optional.
  const homeDone = standalone || !isIOS;

  async function test() {
    setSent('sending');
    // This phone only (Gary, 19 Oct): register it first, then test its own registration, so another device of theirs
    // can never answer for it.
    const userId = session?.user.id;
    if (userId) await registerPush(userId);
    const endpoint = await thisEndpoint();
    if (!endpoint) { setSent('failed'); setRound((n) => n + 1); return; }
    const { data, error } = await supabase.functions.invoke('messaging-send', { body: { action: 'test_push', endpoint } });
    setSent(!error && ((data as { sent?: number } | null)?.sent ?? 0) > 0 ? 'sent' : 'failed');
    setRound((n) => n + 1);
  }

  let n = 0;
  return (
    <div className="chats-app">
      <header className="chats-topbar">
        <h1 className="chats-topbar-title">Set up CMB Chats</h1>
      </header>
      <main className="chats-screen chats-setup">
        <p className="chats-setup-intro">Two minutes, once. New leads must reach you the moment they arrive, like a WhatsApp message does. Need more help? Open the <Link className="chats-link" to="/guide" state={{ fromApp: true }}>step-by-step guide</Link>.</p>
        <OpenInBrowser />

        {/* Inside WhatsApp, Telegram, Instagram or Facebook, or Chrome on iPhone, nothing below can work: only the way
            out shows, and "Not now" (Gary, 19 Oct). */}
        {!(inAppBrowser || iosNotSafari) && <>
        {isIOS && (
          <Step n={++n} done={standalone} title="Add CMB Chats to your home screen">
            {standalone ? <p>Done. You opened it from your home screen.</p> : (
              <>
                <HomeScreenSteps />
                <p>If you already added it and it opens with an address bar, delete that icon and add it again from Safari.</p>
              </>
            )}
          </Step>
        )}

        <Step n={++n} done={notifyOn} title="Allow alerts">
          {notifyOn ? <p>Done.</p>
            : notify === 'denied' ? (
              // Android in a Chrome tab: one blocked site is undone in Chrome's site settings, not under Apps (mobile
              // review, 19 Oct).
              !isIOS && !standalone
                ? <p>Alerts are blocked for this site. Tap the icon left of the address bar, then <b>Permissions</b> (or <b>Notifications</b>), choose <b>Allow</b>, then reload this page.</p>
                : <p>Alerts are blocked. Open your phone's <b>Settings</b>, find <b>CMB Chats</b>{isIOS ? ' under Notifications' : ' under Apps'}, and allow notifications. Then come back here.</p>
            ) : !homeDone ? (
              <p>On iPhone this works once CMB Chats is open from your home screen. Do step 1 first.</p>
            ) : notify === 'unsupported' ? (
              // On iPhone this is only reached from the home screen (homeDone): an iOS older than 16.4 (mobile review, 19 Oct).
              <p>This browser cannot show alerts. {isIOS ? 'Your iPhone needs iOS 16.4 or later for alerts. Update it in Settings, General, Software Update, then open CMB Chats again.' : 'Open this page in Chrome.'}</p>
            ) : (
              <>
                <p>Tap below and choose <b>Allow</b>.</p>
                <button type="button" className="chats-btn chats-step-btn" onClick={() => void askNotifications()}>Allow alerts</button>
              </>
            )}
        </Step>

        <Step n={++n} done={tapped} title="Tap a test alert (optional)">
          {tapped ? <p>Done. This phone now gets your new leads.</p> : !notifyOn || !homeDone ? <p>Once alerts are allowed, Sentinel sends you a test.</p> : (
            <>
              <p>{sent === 'sent' ? 'Sent. Lock your phone. When the alert arrives, tap it: that finishes set-up.' : 'Sentinel sends a test the same way it sends a new lead. Tap it when it arrives.'}</p>
              {sent === 'failed' && <p className="chats-setup-warn">Sentinel could not reach this phone. Close CMB Chats fully, open it again{isIOS ? ' from your home screen' : ''}, then send another test.</p>}
              {sent === 'sent' ? <AlertCheck key={round} onRetest={() => void test()} />
                : <button type="button" className="chats-btn chats-step-btn" disabled={sent === 'sending'} onClick={() => void test()}>{sent === 'sending' ? 'Sending...' : 'Send me a test alert'}</button>}
            </>
          )}
        </Step>

        {!isIOS && !standalone && (
          <Step n={++n} done={false} title="Add to your home screen (optional)">
            <p>Opens CMB Chats like an app. Alerts work without it.</p>
            <HomeScreenSteps canInstall={canInstall} install={() => void install()} />
          </Step>
        )}

        </>}

        <div className="chats-pad">
          {/* The test is optional (Gary, 19 Oct): alerts on is enough to go in. */}
          {!(inAppBrowser || iosNotSafari) && <button type="button" className="chats-btn" disabled={!(notifyOn && homeDone)} onClick={onDone}>Go to my chats</button>}
          {/* Never a trap (Gary, 19 Oct): anyone can carry on, and Sentinel keeps their leads with head office. */}
          <button type="button" className="chats-setup-skip" onClick={onDone}>Not now. Your new leads go to head office until you finish.</button>
        </div>
      </main>
    </div>
  );
}
